import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { X } from 'lucide-react';
import { cases, users } from '../../data/sampleData';

interface NewMessageDialogProps {
  open: boolean;
  onClose: () => void;
  onStartConversation: (participantIds: string[], caseId: string) => void;
}

export const NewMessageDialog: React.FC<NewMessageDialogProps> = ({
  open,
  onClose,
  onStartConversation,
}) => {
  const [selectedCase, setSelectedCase] = useState<typeof cases[0] | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<typeof users[0] | null>(null);
  const currentUser = users[0];

  // Group participants by role
  const groupedParticipants = useMemo(() => {
    const participants = users.filter(user => user.id !== currentUser.id);
    return participants.reduce((acc, user) => {
      const category = user.role === 'client' ? 'Clients' : 'Company Staff';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(user);
      return acc;
    }, {} as Record<string, typeof users>);
  }, [currentUser.id]);

  const handleSubmit = () => {
    if (selectedCase && selectedRecipient) {
      onStartConversation([currentUser.id, selectedRecipient.id], selectedCase.id);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedCase(null);
    setSelectedRecipient(null);
    onClose();
  };

  // Check if recipient is valid for selected case
  const isRecipientValid = (recipient: typeof users[0] | null) => {
    if (!recipient || !selectedCase) return true;
    return recipient.role !== 'client' || selectedCase.clientId === recipient.id;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle 
        sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          fontSize: '1.25rem',
          fontWeight: 600,
        }}
      >
        New Message
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Autocomplete
            value={selectedCase}
            onChange={(_, newValue) => {
              setSelectedCase(newValue);
              // Clear recipient if they're not valid for the new case
              if (selectedRecipient && !isRecipientValid(selectedRecipient)) {
                setSelectedRecipient(null);
              }
            }}
            options={cases}
            getOptionLabel={(option) => `${option.caseNumber} - ${option.title}`}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select Case" 
                required 
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>
                    {option.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={option.caseNumber} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={option.practiceArea} 
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>
            )}
          />

          <Autocomplete
            value={selectedRecipient}
            onChange={(_, newValue) => setSelectedRecipient(newValue)}
            options={Object.values(groupedParticipants).flat()}
            getOptionLabel={(option) => option.name}
            groupBy={(option) => option.role === 'client' ? 'Clients' : 'Company Staff'}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select Recipient" 
                required
                error={!isRecipientValid(selectedRecipient)}
                helperText={
                  !isRecipientValid(selectedRecipient) 
                    ? "This client is not associated with the selected case" 
                    : undefined
                }
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {option.role}
                    {option.role === 'client' && selectedCase && option.id !== selectedCase.clientId && (
                      <span>(not associated with selected case)</span>
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
            getOptionDisabled={(option) => !isRecipientValid(option)}
          />

          {selectedCase && selectedRecipient && (
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Message Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Case
                  </Typography>
                  <Typography>
                    {selectedCase.title}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Recipient
                  </Typography>
                  <Typography>
                    {selectedRecipient.name} ({selectedRecipient.role})
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedCase || !selectedRecipient || !isRecipientValid(selectedRecipient)}
        >
          Start Conversation
        </Button>
      </DialogActions>
    </Dialog>
  );
};